// ImageViewer.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import './ImageViewer.scss';

const ImageViewer = ({
  images,
  initialIndex = 0,
  onClose,
  isOpen = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [imageList, setImageList] = useState([]);
  const [zoomState, setZoomState] = useState({
    scale: 1,
    position: { x: 0, y: 0 }
  });
  // Добавляем состояние для отслеживания смены изображения
  const [isChangingImage, setIsChangingImage] = useState(false);
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const imageNaturalSize = useRef({ width: 0, height: 0 });
  const objectUrls = useRef(new Map());
  // Добавляем ref для хранения таймера смены изображения
  const changeImageTimer = useRef(null);

  // Преобразование входных данных в массив
  useEffect(() => {
    if (images) {
      const list = Array.isArray(images) ? images : [images];
      setImageList(list);
      setCurrentIndex(Math.min(initialIndex, list.length - 1));
    }
  }, [images, initialIndex]);

  // Обработка клавиш
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case 'ArrowLeft':
          navigateToPrev();
          break;
        case 'ArrowRight':
          navigateToNext();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          resetZoom();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, imageList.length]);

  // Блокировка скролла body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (changeImageTimer.current) {
        clearTimeout(changeImageTimer.current);
      }
    };
  }, []);

  // Получение URL для отображения изображения
  const getDisplayUrl = useCallback((image) => {
    if (image instanceof File) {
      if (!objectUrls.current.has(image)) {
        const url = URL.createObjectURL(image);
        objectUrls.current.set(image, url);
      }
      return objectUrls.current.get(image);
    }
    return image;
  }, []);

  // Получение URL для скачивания
  const getDownloadUrl = useCallback((image) => {
    if (image instanceof File) {
      if (!objectUrls.current.has(image)) {
        const url = URL.createObjectURL(image);
        objectUrls.current.set(image, url);
      }
      return objectUrls.current.get(image);
    }
    return image;
  }, []);

  // Получение имени файла для скачивания
  const getFileName = useCallback((image) => {
    if (image instanceof File) {
      return image.name;
    }
    if (typeof image === 'string') {
      try {
        const urlParts = image.split('/');
        const fileName = urlParts[urlParts.length - 1].split('?')[0];
        return fileName || `image-${currentIndex + 1}.jpg`;
      } catch {
        return `image-${currentIndex + 1}.jpg`;
      }
    }
    return `image-${currentIndex + 1}.jpg`;
  }, [currentIndex]);

  // Очистка object URLs при размонтировании
  useEffect(() => {
    return () => {
      objectUrls.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      objectUrls.current.clear();
    };
  }, []);

  const handleClose = () => {
    resetZoom();
    if (onClose) onClose();
  };

  // ИСПРАВЛЕНО: Плавная смена изображения без дерганья
  const changeImage = (newIndex) => {
    // Устанавливаем флаг смены изображения
    setIsChangingImage(true);
    
    // Мгновенно сбрасываем зум в состояние по умолчанию
    setZoomState({
      scale: 1,
      position: { x: 0, y: 0 }
    });
    
    // Останавливаем перетаскивание
    isDragging.current = false;
    
    // Меняем индекс изображения
    setCurrentIndex(newIndex);
    
    // Сбрасываем флаг смены изображения после короткой задержки
    // Это позволяет избежать мерцания при рендере
    if (changeImageTimer.current) {
      clearTimeout(changeImageTimer.current);
    }
    
    changeImageTimer.current = setTimeout(() => {
      setIsChangingImage(false);
    }, 50);
  };

  const navigateToPrev = () => {
    if (imageList.length > 1) {
      const newIndex = currentIndex > 0 ? currentIndex - 1 : imageList.length - 1;
      changeImage(newIndex);
    }
  };

  const navigateToNext = () => {
    if (imageList.length > 1) {
      const newIndex = currentIndex < imageList.length - 1 ? currentIndex + 1 : 0;
      changeImage(newIndex);
    }
  };

  const resetZoom = () => {
    setZoomState({
      scale: 1,
      position: { x: 0, y: 0 }
    });
    isDragging.current = false;
  };

  const handleZoomIn = () => {
    setZoomState(prev => {
      const newScale = Math.min(prev.scale + 0.5, 3);
      return {
        scale: newScale,
        position: prev.position
      };
    });
  };

  const handleZoomOut = () => {
    setZoomState(prev => {
      const newScale = Math.max(prev.scale - 0.5, 1);
      
      if (newScale === 1) {
        return {
          scale: 1,
          position: { x: 0, y: 0 }
        };
      }
      
      return {
        scale: newScale,
        position: prev.position
      };
    });
  };

  // Функция для расчета ограничений перетаскивания
  const getDragConstraints = useCallback(() => {
    if (!imageRef.current || !contentRef.current) {
      return { maxX: 0, maxY: 0 };
    }

    const containerRect = contentRef.current.getBoundingClientRect();
    const imageRect = imageRef.current.getBoundingClientRect();
    
    const overflowX = Math.max(0, (imageRect.width - containerRect.width) / 2);
    const overflowY = Math.max(0, (imageRect.height - containerRect.height) / 2);
    
    return {
      maxX: overflowX,
      maxY: overflowY
    };
  }, []);

  // Обработка перетаскивания изображения при увеличении
  const handleMouseDown = (e) => {
    if (zoomState.scale <= 1 || isChangingImage) return;
    
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX - zoomState.position.x,
      y: e.clientY - zoomState.position.y
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || zoomState.scale <= 1 || isChangingImage) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;
    
    const constraints = getDragConstraints();
    
    setZoomState(prev => ({
      ...prev,
      position: {
        x: Math.max(-constraints.maxX, Math.min(constraints.maxX, newX)),
        y: Math.max(-constraints.maxY, Math.min(constraints.maxY, newY))
      }
    }));
  };

  const handleMouseUp = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    isDragging.current = false;
  };

  // Обработчик клика по оверлею
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Обработчик скачивания
  const handleDownload = async (e) => {
    e.stopPropagation();
    const currentImage = imageList[currentIndex];
    
    try {
      const downloadUrl = getDownloadUrl(currentImage);
      const fileName = getFileName(currentImage);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      
      if (typeof currentImage === 'string') {
        window.open(currentImage, '_blank');
      }
    }
  };

  // Определение типа файла (изображение или видео)
  const isVideoFile = useCallback((file) => {
    if (file instanceof File) {
      return file.type.startsWith('video/');
    }
    if (typeof file === 'string') {
      const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
      const extension = file.split('?')[0].split('.').pop()?.toLowerCase();
      return videoExtensions.includes(extension);
    }
    return false;
  }, []);

  if (!isOpen || imageList.length === 0) return null;

  const currentImage = imageList[currentIndex];
  const displayUrl = getDisplayUrl(currentImage);
  const isVideo = isVideoFile(currentImage);
  
  const isImageType = currentImage instanceof File 
    ? currentImage.type.startsWith('image/')
    : typeof currentImage === 'string' && 
      /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i.test(currentImage.split('?')[0]);

  return (
    <div className="image-viewer-overlay" onClick={handleOverlayClick}>
      <div 
        className="image-viewer-container"
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="image-viewer-header">
          <div className="image-viewer-info">
            {imageList.length > 1 && (
              <span className="image-counter">
                {currentIndex + 1} / {imageList.length}
              </span>
            )}
          </div>
          <div className="image-viewer-controls">
            <button className="control-btn" onClick={handleZoomIn} title="Увеличить (+)">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
            </button>
            <button className="control-btn" onClick={handleZoomOut} title="Уменьшить (-)">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
            </button>
            <button className="control-btn" onClick={resetZoom} title="Сбросить масштаб (0)">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
              </svg>
            </button>
            <button className="control-btn" onClick={handleDownload} title="Скачать">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
              </svg>
            </button>
            <button className="control-btn close" onClick={handleClose} title="Закрыть (Esc)">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Навигация */}
        {imageList.length > 1 && (
          <>
            <button 
              className="nav-btn prev" 
              onClick={(e) => { e.stopPropagation(); navigateToPrev(); }}
              title="Предыдущее (←)"
            >
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
            <button 
              className="nav-btn next" 
              onClick={(e) => { e.stopPropagation(); navigateToNext(); }}
              title="Следующее (→)"
            >
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
          </>
        )}

        {/* Контент */}
        <div 
          className="image-viewer-content"
          ref={contentRef}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {!isVideo ? (
            <div className="image-container">
              <img
                ref={imageRef}
                src={displayUrl}
                alt={`Image ${currentIndex + 1}`}
                className={`viewer-image ${isChangingImage ? 'changing' : ''}`}
                style={{
                  transform: zoomState.scale === 1 
                    ? 'none' 
                    : `translate(${zoomState.position.x}px, ${zoomState.position.y}px) scale(${zoomState.scale})`,
                  cursor: zoomState.scale > 1 && !isChangingImage ? 'grab' : 'default',
                  transition: isDragging.current || isChangingImage ? 'none' : 'transform 0.1s ease'
                }}
                onMouseDown={handleMouseDown}
                onDragStart={(e) => e.preventDefault()}
              />
            </div>
          ) : (
            <div className="video-container">
              <video
                src={displayUrl}
                controls
                className={`viewer-video ${isChangingImage ? 'changing' : ''}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;